import { FC, useCallback, useContext, useEffect, useMemo } from 'react';

import './ISCOArchetypesComponent.scss';

import ParentISCOChart from './ParentISCOChart';
import CountryFlag from './CountryFlag';
import { SelectCountry } from '@components/Input/Select';
import { ISCOArchetypeContext } from '@providers/ISCOArchetype';
import { Cancel } from '@mui/icons-material';
import Tabs from '@components/Tabs';
import useViewportChange from '@helpers/useViewportChange';

type ISCOArchetypesProps = {
    fetchData: () => void;
    countryCode: string;
    setSelectedProfile: any;
};

const ISCOArchetypesComponent: FC<ISCOArchetypesProps> = ({ fetchData, countryCode, setSelectedProfile }) => {
    const { isMobile } = useViewportChange();
    const { setIsISCOPopupShown, showISCODetails, showSkillsDetails, setSelectedChildISCO, setSelectedParentISCO, setShowISCODetails, setShowSkillsDetails } = useContext(ISCOArchetypeContext);

    const tabsTitles = useMemo(() => {
        return [
            {
                id: 'citizenView',
                label: 'Citizen View',
                display: true
            }
        ]
    }, []);

    const selectedTab = tabsTitles[0]?.id;

    const handleTabChange = (id: string) => {
        // Code to be added in case of multiple tabs
    };

    const handleCloseArchetypePopup = useCallback(() => {
        setIsISCOPopupShown && setIsISCOPopupShown(false);
        setSelectedParentISCO && setSelectedParentISCO(null);
        setSelectedChildISCO && setSelectedChildISCO(null);
        setShowISCODetails && setShowISCODetails(false);
        setShowSkillsDetails && setShowSkillsDetails(false);
        fetchData();
    }, [fetchData, setIsISCOPopupShown, setSelectedChildISCO, setSelectedParentISCO, setShowISCODetails, setShowSkillsDetails]);

    // On exit of component reset back all of the states
    useEffect(() => {
        return () => {
            // This is the cleanup function that runs on component unmount
            setIsISCOPopupShown && setIsISCOPopupShown(false);
            setSelectedChildISCO && setSelectedChildISCO(null);
            setSelectedParentISCO && setSelectedParentISCO(null);
            setShowISCODetails && setShowISCODetails(false);
            setShowSkillsDetails && setShowSkillsDetails(false);
        };
    }, [setIsISCOPopupShown, setSelectedChildISCO, setSelectedParentISCO, setShowISCODetails, setShowSkillsDetails]); // The empty dependency array ensures this runs only on mount and unmount


    return <div className="country-details archetypes-details p-2">
        {!showISCODetails && !showSkillsDetails &&
            <div className="archetypes-categories">
                <div className="archetypes-country">
                    <CountryFlag countryCode={countryCode} /> <SelectCountry setSelectedProfile={setSelectedProfile} />
                </div>
                <h2>Impact</h2>
                <div className="archetypes-category-list">
                    <div className="archetypes-category">
                        <div className="archetypes-title">
                            <h3>Shrinking Jobs</h3>
                        </div>
                        <h4>Occupations projected to experience a decline in demand resulting from the rise of AI, either through direct full or partial job replacement, or significant increases in productivity from AI complementarity.</h4>
                    </div>
                    <div className="archetypes-category">
                        <div className="archetypes-title">
                            <h3>Stable Jobs</h3>
                        </div>
                        <h4>Occupations expected to maintain consistent demand, offering steady employment with minimal impact from AI. </h4>
                    </div>
                    <div className="archetypes-category">
                        <div className="archetypes-title">
                            <h3>Growing Jobs</h3>
                        </div>
                        <h4>Occupations that will likely see an increase in demand, marked by expanding opportunities and favorable employment prospects driven by the rise of AI.</h4>
                    </div>
                </div>
                <h2>Job posts for past year</h2>
                <div className="archetypes-jobs-count-list">
                    <div className="archetypes-jobs-count">
                        <div className="archetypes-jobs-count-title">High</div>
                        <div className="archetypes-jobs-count-circle-container">
                            <div className="archetypes-jobs-count-circle high"></div>
                        </div>
                    </div>
                    <div className="archetypes-jobs-count">
                        <div className="archetypes-jobs-count-title">Medium</div>
                        <div className="archetypes-jobs-count-circle-container">
                            <div className="archetypes-jobs-count-circle medium"></div>
                        </div>
                    </div>
                    <div className="archetypes-jobs-count">
                        <div className="archetypes-jobs-count-title">Low</div>
                        <div className="archetypes-jobs-count-circle-container">
                            <div className="archetypes-jobs-count-circle low"></div>
                        </div>
                    </div>
                </div>
            </div>
        }
        <div className={"archetypes-container " + ((showISCODetails || showSkillsDetails) ? "full-width" : "")}>
            <Cancel type="button" onClick={handleCloseArchetypePopup} className="country-details__close-button" />
            <Tabs
                tabs={tabsTitles}
                selectedTab={selectedTab}
                contentStyle={{
                    height: `calc(100vh - ${isMobile ? 62 : 172}px)`,
                    overflowX: "auto"
                }}
                onChange={handleTabChange}
            >
                <ParentISCOChart countryCode={countryCode} />
            </Tabs>
        </div>
    </div>
};

export default ISCOArchetypesComponent;
